// NEW_FILE_JUSTIFIED: Builder state machine for the new insurance type builder; reducer + hook
import { useReducer, useCallback } from 'react';
import type {
  IBuilderField,
  IBuilderSection,
  IBuilderType,
  IBuilderMeta,
  IBuilderState,
  TBuilderAction,
  TBuilderFieldType,
  TSectionColumns,
  TBuilderSelection,
} from '@/@types/insuranceBuilder';
import { getFieldMeta } from '@/lib/insurance/fieldTypeRegistry';

export type { IBuilderMeta, IBuilderState, TBuilderAction };

const EMPTY_STATE: IBuilderState = {
  meta: { providerId: undefined, name: '', supportsDrivers: false },
  sections: [],
  selection: null,
  isDirty: false,
  aiKeys: undefined,
};

function reorderByKeys<T extends { sortOrder: number }>(
  items: T[],
  idKey: keyof T,
  orderedIds: string[]
): T[] {
  const map = new Map(items.map(i => [i[idKey] as unknown as string, i]));
  return orderedIds
    .map((id, idx) => {
      const item = map.get(id);
      return item ? { ...item, sortOrder: idx } : null;
    })
    .filter((x): x is T => x !== null);
}

function buildEmptyField(fieldType: TBuilderFieldType, sortOrder: number): IBuilderField {
  const meta = getFieldMeta(fieldType);
  const base: IBuilderField = {
    fieldKey: crypto.randomUUID(),
    label: meta.label,
    fieldType,
    required: false,
    sortOrder,
  };
  if (meta.supportsOptionsSource) {
    return { ...base, optionsSource: 'custom' as const, customOptions: [] };
  }
  return base;
}

function buildEmptySection(columns: TSectionColumns, sortOrder: number): IBuilderSection {
  return {
    sectionKey: crypto.randomUUID(),
    title: 'New section',
    columns,
    sortOrder,
    fields: [],
  };
}

export function builderReducer(state: IBuilderState, action: TBuilderAction): IBuilderState {
  switch (action.type) {
    case 'INIT':
      return {
        meta: action.state.meta,
        sections: action.state.sections,
        selection: null,
        isDirty: false,
      };

    case 'SET_META':
      return { ...state, meta: { ...state.meta, ...action.patch }, isDirty: true };

    case 'ADD_SECTION': {
      const newSection = buildEmptySection(action.columns, state.sections.length);
      return {
        ...state,
        sections: [...state.sections, newSection],
        selection: { kind: 'section', sectionKey: newSection.sectionKey },
        isDirty: true,
      };
    }

    case 'UPDATE_SECTION':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey ? { ...s, ...action.patch } : s
        ),
        isDirty: true,
      };

    case 'DELETE_SECTION': {
      const nextSelection: TBuilderSelection =
        state.selection?.kind === 'section' && state.selection.sectionKey === action.sectionKey
          ? null
          : state.selection;
      return {
        ...state,
        sections: state.sections
          .filter(s => s.sectionKey !== action.sectionKey)
          .map((s, idx) => ({ ...s, sortOrder: idx })),
        selection: nextSelection,
        isDirty: true,
      };
    }

    case 'REORDER_SECTIONS':
      return {
        ...state,
        sections: reorderByKeys(state.sections, 'sectionKey', action.orderedKeys),
        isDirty: true,
      };

    case 'ADD_FIELD': {
      const targetSection = state.sections.find(s => s.sectionKey === action.sectionKey);
      if (!targetSection) return state;
      const newField = buildEmptyField(action.fieldType, targetSection.fields.length);
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey ? { ...s, fields: [...s.fields, newField] } : s
        ),
        selection: { kind: 'field', sectionKey: action.sectionKey, fieldKey: newField.fieldKey },
        isDirty: true,
      };
    }

    case 'UPDATE_FIELD':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey
            ? {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, ...action.patch } : f
                ),
              }
            : s
        ),
        isDirty: true,
      };

    case 'DELETE_FIELD': {
      const nextSelection: TBuilderSelection =
        state.selection?.kind === 'field' && state.selection.fieldKey === action.fieldKey
          ? null
          : state.selection;
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey
            ? {
                ...s,
                fields: s.fields
                  .filter(f => f.fieldKey !== action.fieldKey)
                  .map((f, idx) => ({ ...f, sortOrder: idx })),
              }
            : s
        ),
        selection: nextSelection,
        isDirty: true,
      };
    }

    case 'REORDER_FIELDS':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey
            ? { ...s, fields: reorderByKeys(s.fields, 'fieldKey', action.orderedKeys) }
            : s
        ),
        isDirty: true,
      };

    case 'MOVE_FIELD': {
      let movedField: IBuilderField | undefined;
      const withoutField = state.sections.map(s => {
        if (s.sectionKey !== action.fromSectionKey) return s;
        movedField = s.fields.find(f => f.fieldKey === action.fieldKey);
        return {
          ...s,
          fields: s.fields
            .filter(f => f.fieldKey !== action.fieldKey)
            .map((f, idx) => ({ ...f, sortOrder: idx })),
        };
      });
      if (!movedField) return state;
      const captured = movedField;
      const withField = withoutField.map(s => {
        if (s.sectionKey !== action.toSectionKey) return s;
        const next = [...s.fields];
        next.splice(action.toIndex, 0, captured);
        return { ...s, fields: next.map((f, idx) => ({ ...f, sortOrder: idx })) };
      });
      return { ...state, sections: withField, isDirty: true };
    }

    case 'SELECT':
      return { ...state, selection: action.selection };

    case 'MARK_SAVED':
      return { ...state, isDirty: false };

    case 'SET_FIELD_ASK_VIA_AI':
      return {
        ...state,
        sections: state.sections.map(s =>
          s.sectionKey === action.sectionKey
            ? {
                ...s,
                fields: s.fields.map(f =>
                  f.fieldKey === action.fieldKey ? { ...f, askViaAiAgent: action.value } : f
                ),
              }
            : s
        ),
        isDirty: true,
      };

    case 'INIT_FROM_AI':
      return {
        meta: action.state.meta,
        sections: action.state.sections,
        selection: null,
        isDirty: false,
        aiKeys: {
          sectionKeys: new Set(action.aiKeys.sectionKeys),
          fieldKeys: new Set(action.aiKeys.fieldKeys),
        },
      };

    default:
      return state;
  }
}

export function useTypeBuilder(initial?: IBuilderType) {
  const initialState: IBuilderState = initial
    ? {
        meta: {
          providerId: initial.providerId,
          name: initial.name,
          supportsDrivers: initial.supportsDrivers ?? false,
        },
        sections: initial.sections,
        selection: null,
        isDirty: false,
      }
    : EMPTY_STATE;

  const [state, dispatch] = useReducer(builderReducer, initialState);

  const init = useCallback((payload: { meta: IBuilderMeta; sections: IBuilderSection[] }) => {
    dispatch({ type: 'INIT', state: payload });
  }, []);

  return { state, dispatch, init };
}
